import type { CompiledMacroGraph } from './compiled';

/** Structural closure only. Cryptographic trust still requires verified exact asset bytes. */
export class GraphCompiledRules {
  public validate(graph: CompiledMacroGraph): string[] {
    const errors: string[] = [];
    const nodes = new Map(graph.nodes.map(node => [node.id, node]));
    const modules = new Map(graph.modules.map(module => [module.moduleId, module]));
    const scripts = new Set(graph.powershellAssets.map(script => script.scriptId));
    const segments = new Set(graph.sequences.map(sequence => sequence.segmentId));
    const junctions = new Map(graph.junctions.map(junction => [junction.id, junction]));
    if (nodes.size !== graph.nodes.length || modules.size !== graph.modules.length || scripts.size !== graph.powershellAssets.length
      || segments.size !== graph.sequences.length || junctions.size !== graph.junctions.length) errors.push('Duplicate compiled identity');
    for (const node of graph.nodes) {
      if (!modules.has(node.moduleId)) errors.push('Missing compiled module');
      if (node.powershellAttachments.some(attachment => !scripts.has(attachment.scriptId))) errors.push('Missing compiled attachment');
    }
    if (graph.entryJunctionIds.some(id => !junctions.has(id))) errors.push('Missing entry junction');
    for (const definition of graph.definitions) {
      const steps = new Map(definition.steps.map(step => [step.id, step]));
      const entry = steps.get(definition.entryStepId);
      if (entry?.kind !== 'node' || nodes.get(entry.nodeId)?.role !== 'entry') errors.push('Missing compiled entry prerequisite');
      for (const step of definition.steps) {
        const targets = step.kind === 'node' ? step.routes.map(route => route.target) : [step.next];
        if (targets.some(target => target.kind === 'step' && !steps.has(target.stepId))) errors.push('Missing compiled flow target');
        if (step.kind === 'segment' ? !segments.has(step.segmentId) : !nodes.has(step.nodeId)) errors.push('Missing compiled step payload');
      }
    }
    for (const edge of graph.edges) {
      if (!graph.definitions.some(definition => definition.id === edge.definitionId && definition.definitionDigest === edge.definitionDigest)) errors.push('Unresolved definition pin');
      if (!junctions.has(edge.fromJunctionId) || !junctions.has(edge.toJunctionId)) errors.push('Missing compiled junction');
    }
    for (const junction of graph.junctions) {
      if (nodes.get(junction.nodeId)?.role !== 'junction') errors.push('Missing compiled junction node');
      const outgoing = graph.edges.filter(edge => edge.fromJunctionId === junction.id).map(edge => edge.id).sort();
      if (JSON.stringify(outgoing) !== JSON.stringify([...junction.outgoingEdgeIds].sort())) errors.push('Compiled outgoing routes disagree');
    }
    return errors;
  }
}
